// src/features/tutor/GroupDetails.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import TutoringSession from './TutoringSession';
import { saveUnitFeedback, saveSessionOverall } from './saveTutorFeedback';
import SelfLearningStatus, { useLearnersProgress } from './SelfLearningStatus';

// Unit history icons. From v2, Overall is given per session, so a present
// learner's unit record has no feedback value and shows ✓ (covered).
// Participation/Preparation are admin-only and are not shown here.
// A no-show is saved as 'absent' (status 'not_present'); both keys map to 🚫.
const FEEDBACK_DISPLAY = {
  great:       { icon: '⭐', label: 'Great' },
  good:        { icon: '👍', label: 'Good' },
  revisit:     { icon: '🔄', label: 'Revisit' },
  absent:      { icon: '🚫', label: 'Not present' },
  not_present: { icon: '🚫', label: 'Not present' }
};

// Format a Firestore Timestamp or seconds-based object to a readable date
function formatDate(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// Check if current time is within any of the group's scheduled session windows
// Window: 15 minutes before session start until 40 minutes after (covers full 30-min session)
function isSessionTime(group) {
  const sessions = group.sessions || [];
  if (sessions.length === 0) return false;

  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }); // e.g. "Monday"
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  return sessions.some(session => {
    if (!session.day || !session.time) return false;
    if (session.day !== currentDay) return false;

    // Parse time string "HH:MM" or "H:MM"
    const [hourStr, minStr] = session.time.split(':');
    const sessionMinutes = parseInt(hourStr, 10) * 60 + parseInt(minStr, 10);

    const windowStart = sessionMinutes - 15;  // 15 min before
    const windowEnd = sessionMinutes + 40;    // 40 min after (covers 30-min session)

    return currentMinutes >= windowStart && currentMinutes <= windowEnd;
  });
}

function GroupDetails({ group, onBack }) {
  const { currentUser } = useAuth();
  const [lessonGroups, setLessonGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  // A lesson run replaces the old per-unit "activeSession": it drives
  // TutoringSession through every unit in a lesson, one at a time, rather
  // than starting a separate session per unit.
  const [activeLessonRun, setActiveLessonRun] = useState(null); // { lessonGroup, units, startIndex }
  const [expandedLessons, setExpandedLessons] = useState({});
  const [showHelp, setShowHelp] = useState(false);

  const learners = group.learners || [];
  // LMS progress of this group's learners — for the Self-learning line.
  const progressByLearner = useLearnersProgress(learners.map(l => l.id));

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);

        if (learners.length === 0) { setLoading(false); return; }

        // 1. Catalog from first learner
        const userCatalogsSnap = await getDocs(query(
          collection(db, 'userCatalogs'),
          where('userId', '==', learners[0].id)
        ));
        const catalogIds = userCatalogsSnap.docs.map(d => d.data().catalogId);
        if (catalogIds.length === 0) { setLoading(false); return; }

        // 2. Lessons
        const lessonsSnap = await getDocs(query(
          collection(db, 'lessons'),
          where('catalogId', 'in', catalogIds)
        ));
        const lessons = lessonsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const lessonIds = lessons.map(l => l.id);
        if (lessonIds.length === 0) { setLoading(false); return; }

        // 3. Tutoring units/packages in batches of 10
        let allTutoringContent = [];
        for (let i = 0; i < lessonIds.length; i += 10) {
          const batch = lessonIds.slice(i, i + 10);
          const [unitsSnap, packagesSnap] = await Promise.all([
            getDocs(query(collection(db, 'units'),
              where('lesson_id', 'in', batch),
              where('requires_tutoring', '==', true))),
            getDocs(query(collection(db, 'packages'),
              where('lessonId', 'in', batch),
              where('requires_tutoring', '==', true)))
          ]);
          allTutoringContent = [
            ...allTutoringContent,
            ...unitsSnap.docs.map(d => ({ id: d.id, ...d.data(), contentType: 'unit' })),
            ...packagesSnap.docs.map(d => ({
              id: d.id, ...d.data(), contentType: 'package',
              lesson_id: d.data().lessonId
            }))
          ];
        }

        // 4. All sessions for this group's learners. status is now one of
        // 'completed' or 'not_present' — there is no 'in_progress' doc
        // anymore, since a unit is only written once the tutor has actually
        // finished giving feedback for it (see handleSaveUnit below).
        const allLearnerIds = learners.map(l => l.id);
        const sessionsSnap = await getDocs(query(
          collection(db, 'tutoring_sessions'),
          where('learner_id', 'in', allLearnerIds),
          where('tutor_id', '==', currentUser.uid)
        ));
        const allSessions = sessionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // 5. Determine status per unit using LATEST session per learner.
        // "Covered" for a learner means their latest record for this unit
        // is either 'completed' or 'not_present' — both close out the unit,
        // a no-show just isn't confused with actual tutor-confirmed coverage
        // anywhere that specifically filters on status == 'completed'.
        const unitsWithStatus = allTutoringContent.map(unit => {
          const unitSessions = allSessions.filter(s => s.unit_id === unit.id);

          const latestPerLearner = allLearnerIds.map(lid => {
            const learnerSessions = unitSessions
              .filter(s => s.learner_id === lid)
              .sort((a, b) => {
                const aTime = a.time_started?.seconds || 0;
                const bTime = b.time_started?.seconds || 0;
                return bTime - aTime; // newest first
              });
            return learnerSessions[0] || null;
          });

          const coveredForAll = latestPerLearner.every(
            s => s && (s.status === 'completed' || s.status === 'not_present')
          );
          const status = coveredForAll ? 'completed' : 'not_started';

          // Session history — one row per past run of this unit
          const closedSessions = unitSessions.filter(
            s => s.status === 'completed' || s.status === 'not_present'
          );
          const runs = [];
          closedSessions
            .sort((a, b) => (a.time_started?.seconds || 0) - (b.time_started?.seconds || 0))
            .forEach(s => {
              const sTime = s.time_started?.seconds || 0;
              const existingRun = runs.find(r =>
                Math.abs(r.time_started?.seconds - sTime) <= 5
              );
              if (existingRun) {
                existingRun.feedbacks.push({ learnerId: s.learner_id, feedback: s.feedback });
              } else {
                runs.push({
                  time_started: s.time_started,
                  feedbacks: [{ learnerId: s.learner_id, feedback: s.feedback }]
                });
              }
            });

          return { ...unit, status, sessionHistory: runs };
        });

        // 6. Group by lesson
        const groupedByLesson = {};
        unitsWithStatus.forEach(unit => {
          const lessonId = unit.lesson_id;
          if (!groupedByLesson[lessonId]) {
            const lesson = lessons.find(l => l.id === lessonId);
            groupedByLesson[lessonId] = {
              lessonId,
              lessonCode: lesson?.code || '',
              lessonTitle: lesson?.title || 'Unknown Lesson',
              lessonObjective: lesson?.lessonObjective || '',
              lessonOrder: lesson?.order || 0,
              catalogOrder: catalogIds.indexOf(lesson?.catalogId ?? ''),
              units: []
            };
          }
          groupedByLesson[lessonId].units.push(unit);
        });

        // 7. Sort lessons; keep each lesson's units in their natural content
        // order (not status order) — a lesson run walks them in this order
        // so "Unit 2 of 5" means the same thing every time.
        const sortedLessonGroups = Object.values(groupedByLesson)
          .sort((a, b) => a.catalogOrder !== b.catalogOrder ? a.catalogOrder - b.catalogOrder : a.lessonOrder - b.lessonOrder)
          .map(g => ({
            ...g,
            units: g.units.sort((a, b) => (a.order || 0) - (b.order || 0))
          }));

        setLessonGroups(sortedLessonGroups);

        // Auto-expand lessons with incomplete units
        const autoExpand = {};
        sortedLessonGroups.forEach(g => {
          autoExpand[g.lessonId] = g.units.some(u => u.status !== 'completed');
        });
        setExpandedLessons(autoExpand);

        // Note: there is no more mid-lesson resume-after-reload. A unit is
        // only saved once its feedback is actually given (no in-progress
        // doc), so a tutor who closes the tab mid-lesson picks the lesson
        // back up from wherever its units currently stand, same as opening
        // it fresh — the units already saved stay saved, nothing is lost,
        // but the browser tab itself isn't restored where it left off.

      } catch (error) {
        console.error('Error fetching group content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, currentUser.uid]);

  // Start (or resume, or redo) a lesson run. startAt lets a click on a
  // specific unit's Preview jump the run to that unit instead of the first
  // incomplete one.
  const handleStartLesson = (lessonGroup, { redo = false, startAt = null } = {}) => {
    const units = lessonGroup.units;
    let startIndex = 0;
    if (startAt !== null) {
      startIndex = Math.max(0, units.findIndex(u => u.id === startAt));
    } else if (!redo) {
      const firstIncomplete = units.findIndex(u => u.status !== 'completed');
      startIndex = firstIncomplete === -1 ? 0 : firstIncomplete;
    }
    setActiveLessonRun({ lessonGroup, units, startIndex });
  };

  // Called once per unit, the moment the tutor finishes giving feedback for
  // it — the only place tutoring_sessions docs get written. No fallback
  // value: a learner without an answer is not saved (see saveTutorFeedback.js).
  const handleSaveUnit = (unit, presenceMap, durationMinutes, extra = {}) =>
    saveUnitFeedback({
      tutorId: currentUser.uid,
      learners,
      groupId: group.id,
      unit,
      presenceMap,
      participationMap: extra.participationMap,
      preparationMap: extra.preparationMap,
      durationMinutes,
      durationSeconds: extra.durationSeconds,
      sittingId: extra.sittingId,
    });

  // Called once when the session is closed — Overall for each learner
  // present in at least one unit.
  const handleSaveSessionOverall = (overallMap, log, sittingId) =>
    saveSessionOverall({
      tutorId: currentUser.uid,
      groupId: group.id,
      lessonTitle: activeLessonRun?.lessonGroup.lessonTitle,
      overallMap,
      log,
      sittingId,
    });

  const handleFinishLesson = () => {
    setActiveLessonRun(null);
    window.location.reload();
  };

  const toggleLesson = (lessonId) => {
    setExpandedLessons(prev => ({ ...prev, [lessonId]: !prev[lessonId] }));
  };

  if (loading) return <div className="learner-details-loading">Loading...</div>;

  if (activeLessonRun) {
    return (
      <TutoringSession
        lessonTitle={activeLessonRun.lessonGroup.lessonTitle}
        units={activeLessonRun.units}
        startIndex={activeLessonRun.startIndex}
        learners={learners}
        onSaveUnit={handleSaveUnit}
        onSaveSessionOverall={handleSaveSessionOverall}
        onFinishLesson={handleFinishLesson}
        onBack={() => setActiveLessonRun(null)}
      />
    );
  }

  return (
    <div className="learner-details">
      <div className="learner-header">
        <button className="back-button" onClick={onBack}>← Back to Groups</button>
        <h2>{group.name}</h2>
        <div className="group-learner-chips">
          {learners.map(l => (
            <span key={l.id} className="learner-chip">{l.name}</span>
          ))}
        </div>
        <button
          onClick={() => setShowHelp(v => !v)}
          aria-label="How sessions work"
          title="How sessions work"
          style={{
            marginLeft: '8px', width: '24px', height: '24px', borderRadius: '50%',
            border: '1px solid #d1d5db', background: showHelp ? '#f0fdfa' : 'white',
            color: '#19a4a0', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            lineHeight: 1, flexShrink: 0
          }}
        >?</button>
      </div>

      {showHelp && (
        <div style={{
          background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: '10px',
          padding: '1rem 1.25rem', margin: '0 0 1rem', fontSize: '0.875rem', color: '#374151'
        }}>
          <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#0f766e' }}>How sessions work now</p>
          <ul style={{ margin: 0, paddingLeft: '1.1rem', lineHeight: 1.6 }}>
            <li>Each lesson has one button — <strong>Start lesson</strong>, <strong>Continue lesson</strong>, or <strong>Redo lesson</strong> — that walks you through every unit in that lesson, one at a time.</li>
            <li>For each unit, answer two things per learner: <strong>Participation</strong> and <strong>Preparation</strong>. Tap <strong>🚫 Not present</strong> for a learner who isn&apos;t there for that unit — the questions are then skipped.</li>
            <li>Units are guidelines — you don&apos;t have to cover them all. When the session is over, tap <strong>End session</strong> (on any unit). You&apos;ll then give the <strong>Overall</strong> feedback (⭐ / 👍 / 🔄) once per learner for the whole session.</li>
            <li>The questions in each unit are guidance for discussion, not a checklist — it&apos;s fine to move on without covering every one.</li>
            <li>Once feedback is given, the button takes you straight to the next unit — no pop-up to confirm. On the last unit it says <strong>End session</strong> instead.</li>
            <li>After ending the session you&apos;ll see a one-time recap of everything covered. Tap <strong>Done</strong> — it&apos;s all already saved by then.</li>
            <li>Each unit saves the moment you give its feedback, so leaving partway through keeps what you&apos;ve already covered. A unit you leave before answering every question isn&apos;t saved.</li>
            <li><strong>Self-learning</strong> under each lesson shows whether each learner opened that lesson&apos;s units in the LMS before the session — use it to talk about their self-learning at the start.</li>
            <li>Rate <strong>Preparation</strong> by what you observe in the session, not by the self-learning line.</li>
          </ul>
        </div>
      )}

      {learners.length === 0 && (
        <div className="empty-state">
          <p>No learners in this group yet.</p>
          <p>Go back and use &quot;Manage&quot; to add learners.</p>
        </div>
      )}

      {learners.length > 0 && lessonGroups.length === 0 && (
        <div className="empty-state">
          <p>No tutoring units found for this group.</p>
          <p>Make sure learners are assigned to a catalog and units are marked with &quot;requires_tutoring&quot;.</p>
        </div>
      )}

      {lessonGroups.length > 0 && (
        <div className="lessons-sections">
          {lessonGroups.map(lessonGroup => {
            const allCompleted = lessonGroup.units.every(u => u.status === 'completed');
            const completedCount = lessonGroup.units.filter(u => u.status === 'completed').length;
            const totalCount = lessonGroup.units.length;

            return (
              <div key={lessonGroup.lessonId} className="lesson-group">
                <div
                  className="lesson-header"
                  onClick={() => toggleLesson(lessonGroup.lessonId)}
                >
                  <div className="lesson-title-section">
                    <span className="toggle-icon">
                      {expandedLessons[lessonGroup.lessonId] ? '▼' : '►'}
                    </span>
                    <div className="lesson-title-block">
                      <h3>
                      {lessonGroup.lessonCode && (
                        <span className="lesson-code">{lessonGroup.lessonCode} </span>
                      )}
                      {lessonGroup.lessonTitle}
                    </h3>
                      {lessonGroup.lessonObjective && (
                        <p className="lesson-objective">{lessonGroup.lessonObjective}</p>
                      )}
                    </div>
                    {allCompleted && <span className="lesson-completed-badge">✓ Complete</span>}
                  </div>
                  <div className="lesson-progress">
                    {completedCount} / {totalCount} units covered
                  </div>
                </div>

                {expandedLessons[lessonGroup.lessonId] && (
                  <>
                    <SelfLearningStatus
                      lessonId={lessonGroup.lessonId}
                      learners={learners}
                      progressByLearner={progressByLearner}
                    />

                    {/* Lesson-level session controls — one button starts or
                        resumes the whole lesson's remaining units in order,
                        replacing the old per-unit Start/Redo buttons. */}
                    <div style={{ padding: '0 1rem 0.75rem', display: 'flex', gap: '8px' }}>
                      <button
                        className={allCompleted ? 'redo-session-button' : 'start-session-button'}
                        onClick={() => handleStartLesson(lessonGroup, { redo: allCompleted })}
                        style={{ flex: 1 }}
                      >
                        {allCompleted
                          ? '🔁 Redo lesson'
                          : completedCount > 0
                            ? `▶️ Continue lesson (unit ${completedCount + 1} of ${totalCount})`
                            : '▶️ Start lesson'}
                      </button>
                    </div>

                    <div className="units-grid">
                      {lessonGroup.units.map(unit => (
                        <div
                          key={unit.id}
                          className={`unit-card ${unit.status === 'completed' ? 'completed' : ''}`}
                        >
                          {/* Unit header */}
                          <div className="unit-header">
                            <h4>{unit.title}</h4>
                            {unit.status === 'completed' ? (
                              <span className="completed-badge">✓</span>
                            ) : unit.contentType === 'package' && (
                              <span className={`content-badge ${unit.contentType}`}>📦 Package</span>
                            )}
                          </div>

                          {/* Session history — shown on units with at least one past run */}
                          {unit.sessionHistory && unit.sessionHistory.length > 0 && (
                            <div className="session-history">
                              {unit.sessionHistory.map((run, i) => (
                                <div key={i} className="session-history-row">
                                  <span className="session-history-date">
                                    {formatDate(run.time_started)}
                                  </span>
                                  <span className="session-history-feedbacks">
                                    {run.feedbacks.map(({ learnerId, feedback }) => {
                                      const learner = learners.find(l => l.id === learnerId);
                                      const fb = FEEDBACK_DISPLAY[feedback] || (feedback == null ? { icon: '✓', label: 'Covered' } : { icon: '?', label: feedback });
                                      return (
                                        <span key={learnerId} className="session-history-chip" title={learner?.name}>
                                          {fb.icon} {learner?.name?.split(' ')[0] || '?'}
                                        </span>
                                      );
                                    })}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="unit-card-actions">
                            <button
                              className="preview-unit-button"
                              onClick={() => {
                                // During session time, preview also jumps the
                                // lesson run to this unit (if not already running).
                                if (isSessionTime(group) && !activeLessonRun) {
                                  handleStartLesson(lessonGroup, { startAt: unit.id });
                                }
                                window.open(
                                  `/preview-lesson/${lessonGroup.lessonId}?unitId=${unit.id}`,
                                  '_blank'
                                );
                              }}
                              title={
                                isSessionTime(group)
                                  ? "Preview unit — lesson session will start automatically"
                                  : "Preview this unit content"
                              }
                            >
                              👁️ Preview
                              {isSessionTime(group) && (
                                <span style={{
                                  fontSize: '10px',
                                  display: 'block',
                                  opacity: 0.85,
                                  marginTop: '2px'
                                }}>
                                  starts session
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GroupDetails;